import * as Sentry from '@sentry/react';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RecordingErrorModal from '@/components/RecordingErrorModal';
import { useAuthContext } from '@/contexts/authContext';
import {
  MicrophonePermissionState,
  UseMicrophonePermissionReturn,
  useMicrophonePermission,
} from '@/hooks/audio/useMicrophonePermission';
import { isMicNoSignalError, MicNoSignalError } from '@/lib/micHealth/micNoSignalError';
import { noop } from '@/utils/noop';
import { ExpectedMicrophoneAcquisitionFailure, getExpectedMicrophoneAcquisitionFailure } from '@/utils/sentry';

export type MicrophoneRecoverySurface = 'activity_session' | 'conversation' | 'practice' | 'live_session';

interface MicrophonePermissionContextValue extends UseMicrophonePermissionReturn {
  showPermissionModal: boolean;
  handlePermissionError: (error: unknown, surface: MicrophoneRecoverySurface) => boolean;
  dismissPermissionModal: () => void;
  // The last take dropped for a mic with no signal, until dismissed. No UI shows it yet (awaiting design).
  noSignalError: MicNoSignalError | undefined;
  dismissNoSignalError: () => void;
}

const MicrophonePermissionContext = createContext<MicrophonePermissionContextValue | undefined>(undefined);

export interface MicrophonePermissionGuardProps {
  children: React.ReactNode;
  customModal?: React.ReactNode;
  onPermissionDenied?: () => void;
  autoShowModal?: boolean;
  // For a guard inside a full-screen dialog, whose recovery modal must sit above it.
  modalDialogClassname?: string;
}

export const MicrophonePermissionGuard: React.FC<MicrophonePermissionGuardProps> = ({
  children,
  customModal,
  onPermissionDenied,
  autoShowModal = true,
  modalDialogClassname,
}) => {
  const { t } = useTranslation('activitySession');
  const { user } = useAuthContext();
  const micPermission = useMicrophonePermission();
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [recoveryFailure, setRecoveryFailure] = useState<ExpectedMicrophoneAcquisitionFailure>();
  const [noSignalError, setNoSignalError] = useState<MicNoSignalError>();

  const handlePermissionError = useCallback(
    (error: unknown, surface: MicrophoneRecoverySurface) => {
      // `useMediaRecorder` already counted the dead mic and dropped the take; surface it via `useMicNoSignalError`.
      if (isMicNoSignalError(error)) {
        setNoSignalError(error);
        return true;
      }

      const failure = getExpectedMicrophoneAcquisitionFailure(error);
      if (!failure) {
        return false;
      }

      Sentry.metrics.count('microphone.acquisition.expected_failure', 1, {
        attributes: { reason: failure, surface },
      });

      const recoveryLogKey = `microphone-permission-recovery:${failure}:${surface}`;
      if (!sessionStorage.getItem(recoveryLogKey)) {
        Sentry.logger.info('microphone.permission_recovery_shown', { reason: failure, surface, userId: user?.id });
        sessionStorage.setItem(recoveryLogKey, 'true');
      }

      if (autoShowModal) {
        setRecoveryFailure(failure);
        setShowPermissionModal(true);
      }
      onPermissionDenied?.();
      return true;
    },
    [autoShowModal, onPermissionDenied, user?.id]
  );

  const dismissPermissionModal = useCallback(() => {
    setShowPermissionModal(false);
    setRecoveryFailure(undefined);
  }, []);

  const dismissNoSignalError = useCallback(() => setNoSignalError(undefined), []);

  const handleRequestPermissionAgain = useCallback(async () => {
    const currentState = await micPermission.checkPermission();
    if (currentState === MicrophonePermissionState.DENIED) {
      dismissPermissionModal();
      return;
    }

    const granted = await micPermission.requestPermission();
    if (granted) {
      setShowPermissionModal(false);
      setRecoveryFailure(undefined);
    }
  }, [dismissPermissionModal, micPermission]);

  const contextValue: MicrophonePermissionContextValue = {
    ...micPermission,
    showPermissionModal,
    handlePermissionError,
    dismissPermissionModal,
    noSignalError,
    dismissNoSignalError,
  };

  const isPermanentlyDenied =
    recoveryFailure === 'permission_denied' && micPermission.permissionState === MicrophonePermissionState.DENIED;

  const modalTitle = isPermanentlyDenied ? t('error.recording_permission_blocked_title') : undefined;
  const modalDescription = isPermanentlyDenied ? t('error.recording_permission_blocked_description') : undefined;
  const modalButtonText = isPermanentlyDenied ? t('error.recording_permission_blocked_button_text') : undefined;

  return (
    <MicrophonePermissionContext.Provider value={contextValue}>
      {children}

      {customModal ? (
        customModal
      ) : (
        <RecordingErrorModal
          open={showPermissionModal}
          onClose={dismissPermissionModal}
          onButtonClick={handleRequestPermissionAgain}
          errorType={recoveryFailure === 'device_not_found' ? 'no-device' : 'permission'}
          title={modalTitle}
          description={modalDescription}
          buttonText={modalButtonText}
          dialogClassname={modalDialogClassname}
        />
      )}
    </MicrophonePermissionContext.Provider>
  );
};

export const useMicrophonePermissionContext = (): MicrophonePermissionContextValue | null => {
  const context = useContext(MicrophonePermissionContext);
  return context ?? null;
};

/**
 * The mic-no-signal error from the nearest `MicrophonePermissionGuard`, for a UI to show once one is designed.
 * Outside a guard nothing is caught, so `error` stays undefined.
 */
export const useMicNoSignalError = () => {
  const context = useMicrophonePermissionContext();
  return { error: context?.noSignalError, dismiss: context?.dismissNoSignalError ?? noop };
};
